/** UtilityAdmin → SATS: review each class (attendance + grades), submit, then
 * push to the SATS system (dummy). Submitted/pushed classes shown as chips. */
import { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { CheckCircle2, Send, Download, Loader2, Upload, Inbox, RotateCcw, Database } from "lucide-react";
import { toast } from "sonner";

export default function SatsView() {
  const [columns, setColumns] = useState([]);
  const [examKey, setExamKey] = useState("MT1");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");
  const [downloading, setDownloading] = useState("");
  const [detail, setDetail] = useState(null); // {class_name, section}
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/sats/overview", { params: { exam_key: examKey } })
      .then(({ data }) => { setOverview(data); if (!columns.length) setColumns(data.columns || []); })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load SATS overview."))
      .finally(() => setLoading(false));
  }, [examKey, columns.length]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (cls, section) => {
    setDetail({ class_name: cls, section });
    setDetailLoading(true);
    setDetailData(null);
    try {
      const { data } = await api.get("/sats/class", { params: { class_name: cls, section, exam_key: examKey } });
      setDetailData(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load class."); }
    finally { setDetailLoading(false); }
  };

  const act = async (endpoint, cls, section) => {
    setBusyKey(`${cls}-${section}-${endpoint}`);
    try {
      await api.post(`/sats/${endpoint}`, { class_name: cls, section, exam_key: examKey });
      const labels = { submit: "submitted", push: "pushed to SATS", reopen: "reopened" };
      toast.success(`Class ${cls}-${section} ${labels[endpoint]}${endpoint === "push" ? " (simulated)" : ""}.`);
      load();
      if (detail && detail.class_name === cls && detail.section === section) openDetail(cls, section);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Action failed."); }
    finally { setBusyKey(""); }
  };

  const downloadExcel = async (cls, section) => {
    setDownloading(`${cls}-${section}`);
    try {
      const res = await api.get("/sats/class/export", { params: { class_name: cls, section, exam_key: examKey }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `SATS_Class${cls}-${section}_${overview?.exam_short || examKey}.xlsx`;
      a.click(); setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Download failed."); }
    finally { setDownloading(""); }
  };

  const chipClasses = (overview?.classes || []).filter((c) => c.status !== "none");

  return (
    <Layout>
      <div className="space-y-6" data-testid="sats-page">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>SATS</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Review, submit and push class attendance &amp; grades to the SATS system.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {overview?.mocked && <Badge variant="outline" className="border-amber-300 text-amber-600 bg-amber-50 dark:bg-amber-900/20">Dummy integration</Badge>}
            <Select value={examKey} onValueChange={setExamKey}>
              <SelectTrigger className="h-10 w-[200px] rounded-xl" data-testid="sats-exam"><SelectValue /></SelectTrigger>
              <SelectContent>{columns.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Readiness banner with chips (submitted / pushed) */}
        <div className="rounded-2xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/30 p-5" data-testid="sats-banner">
          <div className="flex items-center gap-2 text-slate-800 dark:text-slate-100 font-semibold">
            <Inbox className="h-4 w-4 text-indigo-500" />
            SATS push · {overview?.exam_short || "—"}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {overview ? `${overview.pushed_students} of ${overview.total_students} students pushed to SATS` : "Loading…"}
          </p>
          {chipClasses.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-3">
              {chipClasses.map((c) => {
                const pushed = c.status === "pushed";
                return (
                  <span key={`${c.class_name}-${c.section}`} data-testid={`sats-chip-${c.class_name}-${c.section}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${pushed
                      ? "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
                      : "bg-white dark:bg-slate-900 border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-300"}`}>
                    {pushed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                    {c.label} {pushed ? "· Pushed" : `· by ${c.submitted_by || "staff"}`}
                  </span>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-slate-400 mt-3">No classes submitted yet. Review a class below and submit it to make it eligible for SATS push.</p>
          )}
        </div>

        {/* Class table */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <Table data-testid="sats-class-table">
            <TableHeader>
              <TableRow>
                <TableHead>Class</TableHead>
                <TableHead className="text-center">Students</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
              ) : (overview?.classes || []).map((c) => {
                const k = `${c.class_name}-${c.section}`;
                return (
                  <TableRow key={k} data-testid={`sats-row-${k}`}>
                    <TableCell className="font-medium">{c.label}</TableCell>
                    <TableCell className="text-center">{c.total_students}</TableCell>
                    <TableCell>
                      {c.status === "pushed" ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Pushed</Badge>
                        : c.status === "submitted" ? <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Submitted</Badge>
                        : <Badge variant="outline" className="text-slate-400">Not submitted</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" className="rounded-lg" disabled={c.total_students === 0} onClick={() => openDetail(c.class_name, c.section)} data-testid={`sats-review-${k}`}>Review</Button>
                      <Button size="sm" variant="ghost" className="rounded-lg" disabled={c.total_students === 0 || downloading === k} onClick={() => downloadExcel(c.class_name, c.section)} data-testid={`sats-excel-${k}`}>
                        {downloading === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Class detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setDetailData(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="sats-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail ? `Class ${detail.class_name}-${detail.section}` : ""} · {overview?.exam_short}</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : detailData ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Roll</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-center">Attendance</TableHead>
                      {detailData.subjects.map((s) => <TableHead key={s} className="text-center">{s}</TableHead>)}
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">Grade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailData.students.map((s) => (
                      <TableRow key={s.student_id_key} data-testid={`sats-student-${s.student_id_key}`}>
                        <TableCell>{s.roll_number}</TableCell>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell className="text-center">{s.attendance}</TableCell>
                        {detailData.subjects.map((sub) => <TableCell key={sub} className="text-center">{s.subjects[sub] || "-"}</TableCell>)}
                        <TableCell className="text-center">{s.total != null ? `${s.total}/${s.max_total}` : "-"}</TableCell>
                        <TableCell className="text-center font-semibold">{s.grade}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end">
                <Button variant="ghost" className="rounded-lg" onClick={() => downloadExcel(detail.class_name, detail.section)} data-testid="sats-detail-excel">
                  <Download className="h-4 w-4 mr-2" /> Excel
                </Button>
                {detailData.status !== "none" && (
                  <Button variant="outline" className="rounded-lg" onClick={() => act("reopen", detail.class_name, detail.section)} disabled={busyKey.startsWith(`${detail.class_name}-${detail.section}`)} data-testid="sats-detail-reopen">
                    <RotateCcw className="h-4 w-4 mr-2" /> Reopen
                  </Button>
                )}
                <Button className="rounded-lg" variant={detailData.status === "none" ? "default" : "secondary"} onClick={() => act("submit", detail.class_name, detail.section)} disabled={detailData.status === "pushed" || busyKey === `${detail.class_name}-${detail.section}-submit`} data-testid="sats-detail-submit">
                  {busyKey === `${detail.class_name}-${detail.section}-submit` ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  {detailData.status === "none" ? "Submit" : "Re-submit"}
                </Button>
                <Button className="rounded-lg bg-emerald-600 hover:bg-emerald-700" onClick={() => act("push", detail.class_name, detail.section)} disabled={detailData.status === "none" || busyKey === `${detail.class_name}-${detail.section}-push`} data-testid="sats-detail-push">
                  {busyKey === `${detail.class_name}-${detail.section}-push` ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {detailData.status === "pushed" ? "Pushed ✓" : "Push to SATS"}
                </Button>
              </div>
              {detailData.status === "none" && <p className="text-xs text-slate-400 text-right">Submit this class to make it eligible to push to SATS.</p>}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
